import type L from 'leaflet';
import { getLeaflet } from './leafletHelpers';
import { cssMatrixForCorners, type OverlayCorners } from '../../utils/overlayCorners';

// A plan-map overlay's image, placed on the map by three lat/lng corners
// (the fourth follows — see overlayCorners.ts) instead of the north-aligned
// rectangle L.ImageOverlay only knows how to draw. Same approach as the MIT
// `Leaflet.ImageOverlay.Rotated` plugin: one <img> in a pane, positioned
// with a CSS `matrix()` transform (transform-origin 0 0) recomputed from the
// corners' projected layer points on every viewreset/zoom/move, plus a
// zoomanim handler so it animates with the rest of the map during a zoom
// gesture instead of snapping into place afterward.
//
// Deliberately not an L.ImageOverlay subclass — that class assumes an
// axis-aligned LatLngBounds throughout, which a sheared/rotated placement
// doesn't have. L.Layer is the right base: it gives onAdd/onRemove/getPane/
// getEvents and nothing that assumes a rectangle.
//
// Built with `L.Layer.extend({...})`, the plain-object Leaflet class idiom
// every Leaflet plugin (including the one this mirrors) uses — its
// `initialize`/`onAdd`/etc. methods aren't typed by @types/leaflet, and
// `this._map`/`this._image` are plugin-private state with no public
// interface to type against, so the body below leans on `any` the same way
// the upstream plugin's own untyped JS does, rather than fighting Leaflet's
// class typings for state Leaflet itself never intended to expose.

export interface RotatedImageOverlayOptions {
  pane?: string;
  opacity?: number;
  /** False (default): pointer-events none, a read-only map layer. True:
   *  the placement editor's own preview, draggable and hit-testable. */
  interactive?: boolean;
}

export interface RotatedImageOverlayLayer extends L.Layer {
  setCorners(corners: OverlayCorners): void;
  setOpacity(opacity: number): void;
  setInteractive(interactive: boolean): void;
  setUrl(url: string): void;
  getElement(): HTMLImageElement | null;
}

type RotatedImageOverlayCtor = new (
  url: string,
  imageWidth: number,
  imageHeight: number,
  corners: OverlayCorners,
  options?: RotatedImageOverlayOptions
) => RotatedImageOverlayLayer;

// Built lazily against the real Leaflet module the first time this runs —
// L.Layer doesn't exist until getLeaflet() has required it, which can't
// happen at module load time (SSR / web export touches this file with no
// `window`).
let ctor: RotatedImageOverlayCtor | null = null;

function buildCtor(Leaflet: typeof L): RotatedImageOverlayCtor {
  const proto: any = {
    initialize(
      this: any,
      url: string,
      imageWidth: number,
      imageHeight: number,
      corners: OverlayCorners,
      options?: RotatedImageOverlayOptions
    ) {
      this._url = url;
      this._imageWidth = imageWidth;
      this._imageHeight = imageHeight;
      this._corners = corners;
      Leaflet.Util.setOptions(this, options);
    },

    onAdd(this: any) {
      if (!this._image) this._initImage();
      this.getPane().appendChild(this._image);
      this._reset();
    },

    onRemove(this: any) {
      Leaflet.DomUtil.remove(this._image);
    },

    getEvents(this: any) {
      return {
        viewreset: this._reset,
        zoom: this._reset,
        zoomend: this._reset,
        moveend: this._reset,
        zoomanim: this._animateZoom,
      };
    },

    _initImage(this: any) {
      const img: HTMLImageElement = Leaflet.DomUtil.create(
        'img',
        'leaflet-image-layer leaflet-zoom-animated'
      );
      img.style.position = 'absolute';
      img.style.left = '0';
      img.style.top = '0';
      img.style.transformOrigin = '0 0';
      img.style.width = `${this._imageWidth}px`;
      img.style.height = `${this._imageHeight}px`;
      img.style.pointerEvents = this.options.interactive ? 'auto' : 'none';
      img.draggable = false;
      img.alt = '';
      img.src = this._url;
      img.style.opacity = String(this.options.opacity ?? 1);
      this._image = img;
    },

    _reset(this: any) {
      const map: L.Map | undefined = this._map;
      const img: HTMLImageElement | undefined = this._image;
      if (!map || !img) return;
      const corners: OverlayCorners = this._corners;
      const tl = map.latLngToLayerPoint(corners.topLeft);
      const tr = map.latLngToLayerPoint(corners.topRight);
      const bl = map.latLngToLayerPoint(corners.bottomLeft);
      const matrix = cssMatrixForCorners(tl, tr, bl, this._imageWidth, this._imageHeight);
      img.style.transform = `matrix(${matrix.join(',')})`;
    },

    // Mirrors L.ImageOverlay's own zoomanim handler: projects corners with
    // the map's predicted post-animation layer points — a private API, the
    // same one the core class and its rotated-overlay plugin ancestor use —
    // so the image animates in step with everything else during a zoom
    // gesture instead of jumping to its final position only once the
    // animation ends.
    _animateZoom(this: any, e: L.ZoomAnimEvent) {
      const map: L.Map | undefined = this._map;
      const img: HTMLImageElement | undefined = this._image;
      if (!map || !img) return;
      const corners: OverlayCorners = this._corners;
      const project = (latlng: L.LatLngExpression): L.Point =>
        (map as any)._latLngToNewLayerPoint(latlng, e.zoom, e.center);
      const matrix = cssMatrixForCorners(
        project(corners.topLeft),
        project(corners.topRight),
        project(corners.bottomLeft),
        this._imageWidth,
        this._imageHeight
      );
      img.style.transform = `matrix(${matrix.join(',')})`;
    },

    setCorners(this: any, corners: OverlayCorners) {
      this._corners = corners;
      this._reset();
    },

    setOpacity(this: any, opacity: number) {
      this.options.opacity = opacity;
      if (this._image) (this._image as HTMLImageElement).style.opacity = String(opacity);
    },

    setInteractive(this: any, interactive: boolean) {
      this.options.interactive = interactive;
      if (this._image) {
        (this._image as HTMLImageElement).style.pointerEvents = interactive ? 'auto' : 'none';
      }
    },

    setUrl(this: any, url: string) {
      this._url = url;
      if (this._image) (this._image as HTMLImageElement).src = url;
    },

    getElement(this: any): HTMLImageElement | null {
      return this._image ?? null;
    },
  };
  return (Leaflet.Layer as unknown as { extend: (p: unknown) => RotatedImageOverlayCtor }).extend(
    proto
  );
}

export function createRotatedImageOverlay(
  url: string,
  imageWidth: number,
  imageHeight: number,
  corners: OverlayCorners,
  options?: RotatedImageOverlayOptions
): RotatedImageOverlayLayer {
  const Leaflet = getLeaflet();
  if (!ctor) ctor = buildCtor(Leaflet);
  return new ctor(url, imageWidth, imageHeight, corners, options);
}
